import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import {
  changeQuantityInCart,
  deleteFromCart,
  deleteVoucher,
  getCart,
  postAddToCart,
  postCheckout,
  postVoucher,
  removeFromCart,
} from '@/components/app/cart/services';
import {
  AddToCartTypes,
  CheckoutParams,
  RemoveFromCart,
} from '@/components/app/cart/services/type';

import {
  CHANGE_QUANTITY_IN_CART,
  DELETE_FROM_CART,
  DELETE_VOUCHER,
  GET_CART,
  POST_CHECKOUT,
  POST_VOUCHER,
  REMOVE_FROM_CART,
} from '@/constant/query-key';
import CartModel from '@/models/cart.model';

useGetCart;

export function useGetCart(options?: any): UseQueryResult<CartModel> {
  const isRenewalRef = useRef(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.has('subscription')) {
        isRenewalRef.current = true;
      }
      setIsReady(true);
    }
  }, []);

  const queryResult = useQuery<CartModel>({
    queryKey: [GET_CART],
    queryFn: () => getCart(isRenewalRef.current),
    ...options,
    enabled: options?.enabled !== false && isReady,
    staleTime: isRenewalRef.current ? Infinity : 1000 * 60 * 5,
  });

  useEffect(() => {
    if (isReady && isRenewalRef.current && queryResult.data) {
      isRenewalRef.current = false;

      if (typeof window !== 'undefined') {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [queryResult.data, isReady]);

  return queryResult;
}

export function usePostAddToCart() {
  return useMutation({
    mutationFn: (body: AddToCartTypes) => postAddToCart(body),
    mutationKey: [GET_CART],
  });
}

export function useDeleteFromCart() {
  return useMutation({
    mutationFn: (id: string) => deleteFromCart(id),
    mutationKey: [DELETE_FROM_CART],
  });
}

export function useRemoveFromCart() {
  return useMutation({
    mutationFn: (body: RemoveFromCart) => removeFromCart(body),
    mutationKey: [REMOVE_FROM_CART],
  });
}

export function useChangeQuantityInCart() {
  return useMutation({
    mutationFn: ({ id, quantity }: { id: string; quantity: number }) =>
      changeQuantityInCart(id, quantity),
    mutationKey: [CHANGE_QUANTITY_IN_CART],
  });
}

export function usePostVoucher() {
  return useMutation({
    mutationFn: (code: string) => postVoucher(code),
    mutationKey: [POST_VOUCHER],
  });
}

export function useDeleteVoucher() {
  return useMutation({
    mutationFn: (code: string) => deleteVoucher(code),
    mutationKey: [DELETE_VOUCHER],
  });
}

export function usePostCheckout() {
  return useMutation({
    mutationFn: (params: CheckoutParams) => postCheckout(params),
    mutationKey: [POST_CHECKOUT],
  });
}
